import { useEffect } from 'react';

export const SITE_ORIGIN = 'https://www.michaelrobertellis.com';

export function absoluteUrl(path = '/') {
  if (!path || path === '/') return `${SITE_ORIGIN}/`;
  return `${SITE_ORIGIN}${path.startsWith('/') ? path : `/${path}`}`;
}

function setMeta(attr, key, content) {
  let element = document.head.querySelector(`meta[${attr}="${key}"]`);
  const created = !element;
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attr, key);
    document.head.appendChild(element);
  }
  const previous = element.getAttribute('content');
  element.setAttribute('content', content);
  return () => {
    if (created) {
      element.remove();
    } else if (previous == null) {
      element.removeAttribute('content');
    } else {
      element.setAttribute('content', previous);
    }
  };
}

export function usePageSeo({
  title,
  description,
  path = '/',
  image,
  keywords,
  type = 'website',
  structuredData
}) {
  const structuredKey = structuredData ? JSON.stringify(structuredData) : '';

  useEffect(() => {
    const url = absoluteUrl(path);
    const previousTitle = document.title;
    document.title = title;

    const tags = [
      ['name', 'description', description],
      ['name', 'robots', 'index, follow'],
      ['property', 'og:title', title],
      ['property', 'og:description', description],
      ['property', 'og:url', url],
      ['property', 'og:type', type],
      ['property', 'og:site_name', 'Michael Ellis'],
      ['name', 'twitter:card', image ? 'summary_large_image' : 'summary'],
      ['name', 'twitter:title', title],
      ['name', 'twitter:description', description]
    ];
    if (keywords) tags.push(['name', 'keywords', keywords]);
    if (image) {
      tags.push(['property', 'og:image', image]);
      tags.push(['name', 'twitter:image', image]);
    }

    const restoreMeta = tags.map(([attr, key, content]) => setMeta(attr, key, content));

    let canonical = document.head.querySelector('link[rel="canonical"]');
    const createdCanonical = !canonical;
    const previousCanonical = canonical ? canonical.getAttribute('href') : null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', url);

    let structuredDataElement;
    if (structuredKey) {
      structuredDataElement = document.createElement('script');
      structuredDataElement.type = 'application/ld+json';
      structuredDataElement.textContent = structuredKey;
      document.head.appendChild(structuredDataElement);
    }

    return () => {
      document.title = previousTitle;
      restoreMeta.forEach((restore) => restore());
      if (createdCanonical) {
        canonical.remove();
      } else if (previousCanonical) {
        canonical.setAttribute('href', previousCanonical);
      }
      if (structuredDataElement) structuredDataElement.remove();
    };
  }, [title, description, path, image, keywords, type, structuredKey]);
}
